import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import {
  FiArrowUpRight,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiZap,
} from "react-icons/fi";
import { components, spacing, typography } from "../styles/designSystem";
import { IN_VIEW_OPTIONS, reveal } from "../styles/animations";
import PageHeader from "../components/PageHeader";
import { allInOnePackage, services } from "../data/servicesData";
import { CTA } from "../sections/CTA";
import "../styles/services.css";
import { testimonials } from "../data/testimonialsData";

const ServicesPageBackground = () => (
  <div
    className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
    aria-hidden="true"
  >
    <div className="absolute inset-0 bg-gradient-to-b from-white via-[#fbfaf7] to-white" />

    <div className="services-page-grid absolute inset-x-0 top-0 h-[48rem] opacity-60" />

    <div className="services-page-orb-a absolute -right-40 top-24 h-[30rem] w-[30rem] rounded-full bg-primary/[0.10] blur-3xl" />

    <div className="services-page-orb-b absolute -left-36 top-[44rem] h-[26rem] w-[26rem] rounded-full bg-secondary/[0.08] blur-3xl" />

    <div className="absolute right-[8%] top-[38rem] h-24 w-24 rotate-12 rounded-[2rem] border border-primary/15 bg-primary/[0.035]" />

    <div className="absolute left-[8%] top-72 h-14 w-14 rotate-45 rounded-2xl border border-secondary/15 bg-white/50" />

    <svg
      className="services-page-ring absolute -right-20 top-[75rem] h-64 w-64 text-secondary/[0.07]"
      viewBox="0 0 100 100"
      fill="none"
    >
      <circle
        cx="50"
        cy="50"
        r="42"
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="4 5"
      />
      <circle cx="50" cy="50" r="28" stroke="currentColor" strokeWidth="1" />
    </svg>
  </div>
);

const AllInOnePackage = ({ sectionRef, visible }) => (
  <section
    ref={sectionRef}
    className={`${reveal(
      visible,
    )} services-package group relative mb-40 overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-secondary via-secondary to-primary text-white shadow-2xl`}
  >
    <div
      className="services-package-shine pointer-events-none absolute inset-y-0 left-0 z-20 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent"
      aria-hidden="true"
    />

    <div
      className="services-package-glow pointer-events-none absolute -left-32 -top-32 h-80 w-80 rounded-full bg-primary/30 blur-3xl"
      aria-hidden="true"
    />

    <div
      className="pointer-events-none absolute -bottom-32 right-0 h-80 w-80 rounded-full bg-accent/20 blur-3xl"
      aria-hidden="true"
    />

    <div
      className="services-package-grid pointer-events-none absolute inset-0 opacity-30"
      aria-hidden="true"
    />

    <div className="relative z-10 p-7 md:p-10 lg:p-14">
      <div className="grid gap-10 lg:grid-cols-[1fr,auto] lg:items-end">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.08] px-4 py-2 backdrop-blur-sm">
            <FiZap className="text-accent" aria-hidden="true" />

            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">
              Complete digital partnership
            </span>
          </div>

          <p className="mt-7 text-xs font-semibold uppercase tracking-[0.16em] text-white/55">
            {allInOnePackage.tag}
          </p>

          <h2 className={`${typography.heading.h2} mt-3 max-w-2xl text-white`}>
            {allInOnePackage.title}
          </h2>

          <div className="mt-6 h-1 w-16 rounded-full bg-gradient-to-r from-primary to-accent" />

          <p className="mt-7 max-w-2xl text-base font-light leading-relaxed text-white/75 md:text-lg">
            One connected setup for businesses that do not want to coordinate
            separate consultants, designers, developers, and marketers.
          </p>
        </div>

        <Link
          to={`/contact?service=${encodeURIComponent(allInOnePackage.slug)}`}
          className={`${components.button.primary} w-full justify-center lg:w-auto lg:min-w-[14rem]`}
        >
          Discuss the full package
          <FiArrowUpRight aria-hidden="true" />
        </Link>
      </div>

      <div className="mt-10 border-t border-white/10 pt-8">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {allInOnePackage.inclusions.map((item) => (
            <div
              key={item}
              className="group/item flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.055] p-4 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.11]"
            >
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white transition-transform duration-300 group-hover/item:scale-110">
                <FiCheck size={12} aria-hidden="true" />
              </span>

              <span className="text-sm leading-relaxed text-white/80 transition-colors duration-300 group-hover/item:text-white">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ServiceCard = ({ service, index, visible }) => (
  <article
    className={`services-card group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-secondary/10 bg-white p-7 shadow-lg transition-all duration-700 hover:-translate-y-2 hover:border-primary/30 hover:shadow-2xl md:p-8 ${
      visible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
    }`}
    style={{
      transitionDelay: visible ? `${index * 120}ms` : "0ms",
    }}
  >
    <div
      className="services-card-sheen pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent"
      aria-hidden="true"
    />

    <div
      className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.055] via-transparent to-accent/[0.08] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      aria-hidden="true"
    />

    <div
      className="pointer-events-none absolute -bottom-8 -left-8 h-24 w-24 rotate-45 rounded-[2rem] bg-accent/[0.09] transition-all duration-700 group-hover:rotate-[65deg] group-hover:scale-125"
      aria-hidden="true"
    />

    <div className="relative z-10 flex h-full flex-col">
      {/* Background word */}
      <span className="pointer-events-none absolute -right-10 -top-12 text-[4.5rem] font-black leading-none text-secondary/[0.035] transition-all duration-500 group-hover:scale-110 group-hover:text-primary/[0.07] md:text-[5.5rem]">
        {service.backgroundWord}
      </span>

      <div className="flex items-start justify-between gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-primary/10 bg-primary/10 text-2xl text-primary transition-all duration-500 group-hover:-translate-y-1 group-hover:rotate-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-white">
          {service.icon}
        </div>

        <p
          className={`${typography.card.base} max-w-[8rem] text-right text-secondary/35 group-hover:text-primary`}
        >
          {service.tag}
        </p>
      </div>

      <h3 className="mt-2 text-2xl font-semibold leading-snug text-secondary transition-colors duration-300 group-hover:text-primary">
        {service.title}
      </h3>

      <p className={`${typography.body.small} mt-4`}>{service.text}</p>

      <div className="mt-7 border-t border-secondary/10 pt-6">
        <p className={`${typography.label} text-gray-400`}>What is included</p>

        <ul className="mt-4 space-y-3">
          {service.inclusions.map((item) => (
            <li
              key={item}
              className="group/item flex items-start gap-3 transition-transform duration-200 hover:translate-x-1"
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-all duration-300 group-hover/item:scale-110 group-hover/item:bg-primary group-hover/item:text-white">
                <FiCheck size={10} aria-hidden="true" />
              </span>

              <span className="text-sm leading-relaxed text-gray-600 transition-colors duration-300 group-hover/item:text-secondary">
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto pt-8">
        <Link
          to={`/contact?service=${encodeURIComponent(service.slug)}`}
          className={`${components.button.primary} w-full justify-center text-sm`}
        >
          Discuss your project
          <FiArrowUpRight aria-hidden="true" />
        </Link>
      </div>
    </div>
  </article>
);

const TestimonialSection = ({ sectionRef, visible }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeTestimonial = testimonials[activeIndex];
  const hasMultipleTestimonials = testimonials.length > 1;

  const showPreviousTestimonial = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1,
    );
  };

  const showNextTestimonial = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1,
    );
  };

  if (!activeTestimonial) return null;

  return (
    <section ref={sectionRef} className={`${reveal(visible)} relative mb-40`}>
      <div className="mb-12 text-center">
        <p className={`${typography.label} text-gray-400`}>
          Real collaboration
        </p>

        <h2 className={`${typography.heading.h2} mt-4 text-secondary`}>
          What clients{" "}
          <span className="font-semibold text-primary">experience</span>
        </h2>

        <div className="mx-auto mt-6 h-1 w-14 rounded-full bg-gradient-to-r from-primary to-accent" />
      </div>

      <div className="group relative mx-auto max-w-5xl">
        <div
          className="pointer-events-none absolute -inset-4 rounded-[3rem] bg-gradient-to-br from-primary/15 via-transparent to-accent/20 opacity-60 blur-2xl transition-all duration-700 group-hover:scale-[1.02] group-hover:opacity-100"
          aria-hidden="true"
        />

        <div className="relative overflow-hidden rounded-[2.5rem] border border-secondary/10 bg-white shadow-2xl transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_30px_80px_rgba(79,107,53,0.16)]">
          <div
            className="services-testimonial-lines pointer-events-none absolute -right-16 -top-16 h-64 w-64 opacity-70 transition-transform duration-700 group-hover:rotate-6 group-hover:scale-110"
            aria-hidden="true"
          />

          <div
            className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-primary/[0.08] blur-3xl"
            aria-hidden="true"
          />

          <div
            className="pointer-events-none absolute right-[12%] top-12 h-20 w-20 rotate-12 rounded-[1.75rem] border border-accent/20 bg-accent/[0.05] transition-transform duration-700 group-hover:rotate-[24deg]"
            aria-hidden="true"
          />

          <div
            className="pointer-events-none absolute left-10 top-10 text-[10rem] font-serif leading-none text-primary/[0.07]"
            aria-hidden="true"
          >
            “
          </div>

          <div
            key={activeTestimonial.id}
            className="relative animate-[testimonialFade_500ms_ease-out] px-8 py-12 md:px-14 md:py-16 lg:px-20 lg:py-20"
          >
            <blockquote className="relative mx-auto max-w-4xl text-center text-xl font-light italic leading-relaxed text-gray-700 md:text-2xl lg:text-[1.75rem] lg:leading-relaxed">
              {activeTestimonial.quote}
            </blockquote>

            <div className="mx-auto mt-10 h-px max-w-xl bg-gradient-to-r from-transparent via-secondary/15 to-transparent" />

            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-primary/20 blur-md transition-transform duration-300 group-hover:scale-125" />

                <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-sm font-bold text-white shadow-lg transition-transform duration-300 group-hover:scale-105">
                  {activeTestimonial.initials}
                </div>
              </div>

              <div className="text-left">
                <p className="font-semibold text-secondary">
                  {activeTestimonial.name}
                </p>

                <p className="mt-1 text-sm text-gray-500">
                  {activeTestimonial.role}, {activeTestimonial.company}
                </p>
              </div>
            </div>

            {hasMultipleTestimonials && (
              <div className="mt-10 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={showPreviousTestimonial}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-secondary/10 text-secondary transition-all duration-300 hover:-translate-x-0.5 hover:border-primary hover:bg-primary hover:text-white"
                  aria-label="Show previous testimonial"
                >
                  <FiChevronLeft aria-hidden="true" />
                </button>

                <div className="flex items-center gap-2">
                  {testimonials.map((testimonial, index) => (
                    <button
                      key={testimonial.id}
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        activeIndex === index
                          ? "w-7 bg-primary"
                          : "w-2 bg-secondary/20 hover:bg-secondary/40"
                      }`}
                      aria-label={`Show testimonial ${index + 1}`}
                      aria-current={activeIndex === index ? "true" : undefined}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={showNextTestimonial}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-secondary/10 text-secondary transition-all duration-300 hover:translate-x-0.5 hover:border-primary hover:bg-primary hover:text-white"
                  aria-label="Show next testimonial"
                >
                  <FiChevronRight aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          <div className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-primary via-accent to-secondary transition-transform duration-700 group-hover:scale-x-100" />
        </div>
      </div>
    </section>
  );
};

const ServicesPage = () => {
  const { ref: headerRef, inView: headerInView } = useInView(IN_VIEW_OPTIONS);

  const { ref: packageRef, inView: packageInView } = useInView(IN_VIEW_OPTIONS);

  const { ref: cardsRef, inView: cardsInView } = useInView(IN_VIEW_OPTIONS);

  const { ref: testimonialRef, inView: testimonialInView } =
    useInView(IN_VIEW_OPTIONS);

  const { ref: ctaRef, inView: ctaInView } = useInView(IN_VIEW_OPTIONS);

  return (
    <main className="relative isolate min-h-screen overflow-hidden bg-[#fbfaf7] pt-24 md:pt-32">
      <ServicesPageBackground />

      <section
        className={`${spacing.container} ${spacing.section} relative z-10`}
      >
        <div className="relative mb-20 md:mb-28">
          <PageHeader
            sectionRef={headerRef}
            visible={headerInView}
            label="Services"
            title={
              <>
                Tailored{" "}
                <span className="font-semibold text-primary">
                  digital solutions
                </span>{" "}
                for growing businesses
              </>
            }
            subtitle="Every business is different. We start with a conversation to understand your goals, timeline, and budget, then recommend the most suitable solution."
          />
        </div>

        <AllInOnePackage sectionRef={packageRef} visible={packageInView} />

        <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <p className={`${typography.label} text-primary`}>
              Individual services
            </p>

            <h2
              className={`${typography.heading.h2} mt-4 max-w-2xl text-secondary`}
            >
              Choose one focused service or combine them over time.
            </h2>
          </div>

          <p className={`${typography.body.base} max-w-md`}>
            Each service can stand on its own. You can begin small and add more
            support as your business grows.
          </p>
        </div>

        <div
          ref={cardsRef}
          className="grid auto-rows-fr gap-7 lg:grid-cols-3 mb-40"
        >
          {services.map((service, index) => (
            <ServiceCard
              key={service.slug}
              service={service}
              index={index}
              visible={cardsInView}
            />
          ))}
        </div>

        <TestimonialSection
          sectionRef={testimonialRef}
          visible={testimonialInView}
        />

        <CTA
          sectionRef={ctaRef}
          visible={ctaInView}
          title="Not sure which option fits?"
          subtitle="Send a quick message with where you are right now. We'll figure out the best path together."
        />
      </section>
    </main>
  );
};

export default ServicesPage;
